import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'

const browserImplementsStorageAccessApi = () => {
  return (
    typeof document.requestStorageAccess === 'function' &&
    typeof document.hasStorageAccess === 'function'
  )
}

const canAccessCookies = () => {
  if (!browserImplementsStorageAccessApi()) {
    return Promise.resolve(true)
  }
  return document.hasStorageAccess()
}
/**
 * IMPORTANT: this function MUST be executed as the result of a user action, like
 * a click, or safari will automatically deny the request
 */
const requestAccess = async (): Promise<boolean> => {
  if (!browserImplementsStorageAccessApi()) {
    return Promise.resolve(true)
  }

  return document
    .requestStorageAccess()
    .then(() => true) //  user clicked yes or safari granted access under the hood
    .catch(() => false) // user clicked deny or safari denied access under the hood
}

/**
 * Hook to ask the user for permission to access their browser storage
 * (cookies, local storage, indexdb) when the content is iframed
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/Storage_Access_API
 *
 * Why? Safari blocks all 3rd parth cookies (any cookie with `sameSite: 'none'`)
 * https://webkit.org/blog/10218/full-third-party-cookie-blocking-and-more/
 *
 * Safari, FF, and Edge implement the storage access api. This lets the
 * browser ask the user for access and then allow 3rd party cookie usage.
 * (Safari is currently the only one that actually blocks 3rd party cookies)
 * Chrome is working toward implementing the storage access api
 * and still accepts 3rd party cookies.
 *
 * The login form is currently iframed as part of the Onshape extension flow.
 */
export const useStorage = () => {
  const [requestStorageAccess, setRequestStorageAccess] = useState(false)
  const [cookieAccessDenied, setCookieAccessDenied] = useState(false)
  const [missingCookies] = useState(() => {
    return !Boolean(Cookies.get('iframeForward'))
  })
  const [isIframe] = useState(() =>
    Boolean(
      typeof window !== 'undefined' &&
        window.location !== window.parent.location,
    ),
  )

  const requestCookieAccess = async () => {
    if (!requestStorageAccess) {
      return true
    }

    const requestGranted = await requestAccess()
    if (!requestGranted) {
      setCookieAccessDenied(true)
    }
    return requestGranted
  }

  useEffect(() => {
    canAccessCookies().then((hasAccess) => {
      setRequestStorageAccess(!hasAccess)
    })
  }, [])

  return {
    requestCookieAccess,
    cookieAccessDenied,
    isIframe,
    missingCookies,
  } as const
}
